import React, { useState, useEffect } from 'react'
import { connect } from 'react-redux'
import { Redirect, useLocation } from 'react-router-dom'
import { loadStripe } from '@stripe/stripe-js'
import { Elements } from '@stripe/react-stripe-js'

import SignupStepZero from 'components/Signup/SignupStepZero'
import SignupStepOne from 'components/Signup/SignupStepOne'
import SignupStepTwo from 'components/Signup/SignupStepTwo'
import SignupStepThree from 'components/Signup/SignupStepThree'
import SignupStepFour from 'components/Signup/SignupStepFour'
import Spinner from 'components/ui/Spinner/Spinner'

import { inviteApi } from 'api'
import { logIn, resetAuthError, setNewPassword, authFail } from 'store/actions'
import { setCurrentLanguage } from 'translation'
import history from 'utils/appHistory'

import BgLoginImg from 'assets/woman.jpg'
import LogoLoginImg from 'assets/logo_tr.png'

const signupSteps = {
  zero: SignupStepZero,
  one: SignupStepOne,
  two: SignupStepTwo,
  three: SignupStepThree,
  four: SignupStepFour,
}

const ENV = process.env.NODE_ENV

const stripePublicKey =
  ENV === 'production'
    ? process.env.STRIPE_PUBLIC_KEY_PRODUCTION
    : ENV === 'dev-remote'
    ? process.env.STRIPE_PUBLIC_KEY_DEV_REMOTE
    : process.env.STRIPE_PUBLIC_KEY_DEVELOPMENT

const SignupPage = function SignupPage({
  attributes,
  authFail,
  isDoctor,
  isAuthenticated,
}) {
  const [stripePromise, setStripePromise] = useState(null)
  const [scaleClassIsSet, setScaleClassIsSet] = useState(false)
  const [signupStep, setSignupStep] = useState('')
  const [freeDays, setFreeDays] = useState(30)
  // TODO: Set as USDs & 12.90 if user is in a USA ip address
  const [price, setPrice] = useState('9.90')
  const [currency, setCurrency] = useState('EUR')
  const [password, setPassword] = useState('')
  const [passwordConfirm, setPasswordConfirm] = useState('')
  const [prescriberCode, setPrescriberCode] = useState('')
  const [inviteId, setInviteId] = useState('')

  const [parseParams, setParseParams] = useState(false)
  const [email, setEmail] = useState('')

  const location = useLocation()

  useEffect(() => {
    setStripePromise(loadStripe(stripePublicKey))
  }, [])

  useEffect(() => {
    const checkInviteParam = async () => {
      const searchParams = new URLSearchParams(location.search)
      const inviteIdParam = [...searchParams].find(([key]) => key === 'inviteId')?.[1]
      if (!inviteIdParam) return setParseParams(true), setSignupStep('zero')

      const {
        error,
        result: { invite },
      } = await inviteApi.getInvite(inviteIdParam)

      if (error) {
        const parsedError =
          error.status === 404 || error.status === 422 ? 'INVITE_NOT_FOUND' : error
        return setParseParams(true), setSignupStep('zero'), authFail(parsedError)
      }

      const { license } = invite

      setCurrentLanguage(invite.language || 'fr', true)

      setInviteId(invite.id)
      setEmail(invite.email)

      const patientSubscriptionPrice = license.patientSubscriptionPrice.toFixed(2)
      setPrice(patientSubscriptionPrice)
      setCurrency(license.currency)
      setFreeDays(license.freeDays)
      if (prescriberCode) setPrescriberCode(invite.prescriberCode)

      setParseParams(true)
      setSignupStep('two')
    }

    checkInviteParam()
  }, [location])

  useEffect(() => {
    document.documentElement.classList.add('no_scale_mod')
    setScaleClassIsSet(true)
    return
  }, [])

  const SignupStep = signupSteps[signupStep]

  const showWelcomeVideo = attributes?.permissions?.features?.alwaysOnWelcomeVideo

  const redirectPath =
    showWelcomeVideo && !isDoctor
      ? `/welcome`
      : isDoctor
      ? `/welcome-doctor`
      : '/indication-select'

  return (
    <>
      {isAuthenticated &&
      history.location.path !== redirectPath &&
      signupStep !== 'two' &&
      signupStep !== 'four' &&
      signupStep !== 'three' ? (
        <Redirect to={redirectPath} />
      ) : null}
      <section className="section login_mod">
        {scaleClassIsSet && (
          <>
            <div className="section_bg login_mod">
              <img className="section_bg_img" src={BgLoginImg} alt="section background" />
            </div>
            {parseParams && signupStep && stripePromise ? (
              <div className="section_in login_mod">
                <div className="logo login_mod">
                  <img className="logo_img" src={LogoLoginImg} alt="logo" />
                </div>
                <Elements
                  stripe={stripePromise}
                  options={{
                    fonts: [
                      {
                        family: 'Bariol',
                        // TODO: Temporary solution, figure out why local import 'url(/fonts/Bariol-Regular.woff2)' 'url(./fonts/Bariol-Regular.woff2)' or 'url(fonts/Bariol-Regular.woff2)' does not work
                        src: 'url(https://music-care-storage-dev.s3.amazonaws.com/assets/Bariol-Regular.woff2)',
                        weight: '400',
                      },
                    ],
                  }}
                >
                  <SignupStep
                    setSignupStep={(newSignUpStep) =>
                      Object.keys(signupSteps).includes(newSignUpStep) &&
                      setSignupStep(newSignUpStep)
                    }
                    email={email}
                    setEmail={setEmail}
                    freeDays={freeDays}
                    setFreeDays={setFreeDays}
                    price={price}
                    setPrice={setPrice}
                    currency={currency}
                    setCurrency={setCurrency}
                    password={password}
                    setPassword={setPassword}
                    passwordConfirm={passwordConfirm}
                    setPasswordConfirm={setPasswordConfirm}
                    prescriberCode={prescriberCode}
                    setPrescriberCode={setPrescriberCode}
                    inviteId={inviteId}
                  />
                </Elements>
              </div>
            ) : (
              <Spinner />
            )}
          </>
        )}
      </section>
    </>
  )
}

const mapStateToProps = ({
  auth: { attributes, error, loading, isAuthenticated, isDoctor },
}) => {
  return {
    attributes,
    loading,
    isAuthenticated,
    isDoctor,
    authError: error,
  }
}

const willMapDispatchToProps = {
  authFail,
  logIn,
  resetAuthError,
  setNewPassword,
}

export default connect(mapStateToProps, willMapDispatchToProps)(SignupPage)
